import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {
  useDeleteEmployeeCompetenceMutation,
  usePostEmployeeCompetenceSetAsActiveMutation,
  usePostEmployeeCompetenceSetAsInactiveMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function EmployeeCompetencesOverview() {
  const navigate = useNavigate();

  const [postEmployeeCompetenceSetAsInactive] = usePostEmployeeCompetenceSetAsInactiveMutation();
  const [postEmployeeCompetenceSetAsActive] = usePostEmployeeCompetenceSetAsActiveMutation();
  const [deleteEmployeeCompetence] = useDeleteEmployeeCompetenceMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(settingsRoutes.employeeCompetencesDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        postEmployeeCompetenceSetAsInactive({competenceId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsActive', () =>
        postEmployeeCompetenceSetAsActive({competenceId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteEmployeeCompetence({id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.employeeCompetences.labels.title')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.employeeCompetences.labels.title'),
          actions: [
            {
              type: 'button',
              onClick: () => navigate(settingsRoutes.employeeCompetencesCreate),
              title: i18n.t('entity.employeeCompetences.labels.newCompetence'),
            },
          ],
        }}
        data-testid={testIds.settings.employeeCompetences('settingsTemplate')}
      >
        <DmsDataGrid
          gridCode="employee-competence"
          actionCallback={actionCallback}
          data-testid={testIds.settings.employeeCompetences('employee-competence')}
        />
      </SettingsTemplate>
    </>
  );
}
